// Get the API URL using the same logic as the main API service
function getApiUrl(): string {
  const runtimeEngineUrl = (window as any).__ENV?.VITE_ENGINE_URL;
  const buildTimeEngineUrl = import.meta.env.VITE_ENGINE_URL;
  const resolvedUrl = runtimeEngineUrl || buildTimeEngineUrl || "http://localhost:8080";
  
  console.log("Auth Service API URL Resolution:", {
    runtime: runtimeEngineUrl,
    buildTime: buildTimeEngineUrl,
    resolved: resolvedUrl
  });
  
  return resolvedUrl;
}

const API_URL = getApiUrl();

export interface LoginCredentials {
  username: string;
  password: string;
  mfa_code?: string;
  remember_me?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  full_name: string;
  organization_name?: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  username?: string;
  organization_id: string;
  organization_name?: string;
  roles: string[];
  is_active: boolean;
  is_verified: boolean;
  mfa_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  new_password: string;
}

export interface PasswordChange {
  current_password: string;
  new_password: string;
}

export interface MFASetup {
  secret: string;
  qr_code: string;
  backup_codes: string[];
}

export interface Session {
  session_id: string;
  device: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  last_activity: string;
  expires_at: string;
}

class AuthService {
  private static instance: AuthService;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  private constructor() {
    // Load tokens from localStorage on initialization
    this.loadTokensFromStorage();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private loadTokensFromStorage(): void {
    const storedTokens = localStorage.getItem('authTokens');
    if (storedTokens) {
      const tokens = JSON.parse(storedTokens);
      this.accessToken = tokens.access_token;
      this.refreshToken = tokens.refresh_token;
      this.scheduleTokenRefresh(tokens.expires_in);
    }
  }

  private saveTokensToStorage(tokens: AuthTokens): void {
    this.accessToken = tokens.access_token;
    this.refreshToken = tokens.refresh_token;
    localStorage.setItem('authTokens', JSON.stringify(tokens));
    this.scheduleTokenRefresh(tokens.expires_in);
  }

  private clearTokensFromStorage(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('authTokens');
    localStorage.removeItem('currentUser');
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private scheduleTokenRefresh(expiresIn: number): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Refresh token 5 minutes before expiry
    const refreshTime = (expiresIn - 300) * 1000;
    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshAccessToken();
      }, refreshTime);
    }
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  public async login(credentials: LoginCredentials): Promise<User> {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    if (credentials.mfa_code) {
      formData.append('mfa_code', credentials.mfa_code);
    }

    const response = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    this.saveTokensToStorage(data.tokens);
    localStorage.setItem('currentUser', JSON.stringify(data.user));
    return data.user;
  }

  public async register(data: RegisterData): Promise<User> {
    const response = await fetch(`${API_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const result = await response.json();
    this.saveTokensToStorage(result.tokens);
    localStorage.setItem('currentUser', JSON.stringify(result.user));
    return result.user;
  }

  public async logout(allSessions: boolean = false): Promise<void> {
    const endpoint = allSessions ? '/api/v1/auth/logout-all' : '/api/v1/auth/logout';

    try {
      await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
        },
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokensFromStorage();
    }
  }

  /**
   * Silent logout - clears tokens without calling backend API
   * Useful when token is already expired or invalid
   */
  public logoutSilent(): void {
    this.clearTokensFromStorage();
  }

  public async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) {
      this.clearTokensFromStorage();
      window.location.href = '/login';
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: this.refreshToken,
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const tokens: AuthTokens = await response.json();
      this.saveTokensToStorage(tokens);
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearTokensFromStorage();
      window.location.href = '/login';
    }
  }

  public async getCurrentUser(): Promise<User> {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      return JSON.parse(storedUser);
    }

    const response = await fetch(`${API_URL}/api/v1/auth/me`, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    const user = await response.json();
    localStorage.setItem('currentUser', JSON.stringify(user));
    return user;
  }

  public async updateProfile(data: Partial<User>): Promise<User> {
    const response = await fetch(`${API_URL}/api/v1/auth/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Profile update failed');
    }

    const user = await response.json();
    localStorage.setItem('currentUser', JSON.stringify(user));
    return user;
  }

  public async requestPasswordReset(email: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password reset request failed');
    }
  }

  public async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password reset failed');
    }
  }

  public async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/auth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password change failed');
    }
  }

  public async verifyEmail(token: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/auth/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Email verification failed');
    }
  }

  public async setupMFA(): Promise<MFASetup> {
    const response = await fetch(`${API_URL}/api/v1/auth/mfa/enable`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'MFA setup failed');
    }

    return response.json();
  }

  public async verifyMFA(code: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/auth/mfa/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'MFA verification failed');
    }
  }

  public async disableMFA(password: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/auth/mfa/disable`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'MFA disable failed');
    }
  }

  public async getSessions(): Promise<Session[]> {
    const response = await fetch(`${API_URL}/api/v1/auth/sessions`, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch sessions');
    }

    return response.json();
  }

  public async revokeSession(sessionId: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/auth/sessions/${sessionId}/revoke`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Session revocation failed');
    }
  }

  // Helper method to make authenticated API calls
  public async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${this.accessToken}`,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // If we get a 401, try to refresh the token and retry
    if (response.status === 401 && this.refreshToken) {
      await this.refreshAccessToken();
      
      // Retry the request with the new token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${this.accessToken}`,
        },
      });
    }

    return response;
  }
}

export const authService = AuthService.getInstance();